import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Card, AgGridTable, Button, LoadingSpinner } from 'components/common';
import { FiDownload, FiUsers, FiInfo } from 'react-icons/fi';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

// Colored circle badge for P/J counts
const CountBadge = ({ value, color }) => (
  <span
    className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-xl tw-font-bold tw-text-xs"
    style={{ backgroundColor: color.bg, color: color.text }}
  >
    {value ?? 0}
  </span>
);
CountBadge.propTypes = {
  value: PropTypes.number,
  color: PropTypes.shape({ bg: PropTypes.string, text: PropTypes.string }).isRequired
};
CountBadge.defaultProps = { value: 0 };

const PCell  = ({ value, node }) => node?.rowPinned
  ? <span className="tw-font-extrabold tw-text-violet-800 tw-text-sm">{value ?? 0}</span>
  : <CountBadge value={value} color={{ bg: '#ede9fe', text: '#6d28d9' }} />;

const JCell  = ({ value, node }) => node?.rowPinned
  ? <span className="tw-font-extrabold tw-text-orange-700 tw-text-sm">{value ?? 0}</span>
  : <CountBadge value={value} color={{ bg: '#fff7ed', text: '#c2410c' }} />;

const COCell = ({ value, node }) => (
  <span className={node?.rowPinned
    ? 'tw-font-extrabold tw-text-blue-800 tw-text-base'
    : 'tw-font-bold tw-text-blue-600'
  }>
    {value ?? 0}
  </span>
);

const StatusCell = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="tw-text-gray-400 tw-text-sm">—</span>;
  }
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${
      value ? 'tw-bg-green-100 tw-text-green-700' : 'tw-bg-gray-100 tw-text-gray-600'
    }`}>
      {value ? 'Active' : 'Inactive'}
    </span>
  );
};

PCell.propTypes      = { value: PropTypes.number, node: PropTypes.object };
JCell.propTypes      = { value: PropTypes.number, node: PropTypes.object };
COCell.propTypes     = { value: PropTypes.number, node: PropTypes.object };
StatusCell.propTypes = { value: PropTypes.bool };
PCell.defaultProps   = { value: 0, node: null };
JCell.defaultProps   = { value: 0, node: null };
COCell.defaultProps  = { value: 0, node: null };
StatusCell.defaultProps = { value: null };

const CSEStatisticsTable = ({ data, loading, error }) => {
  const gridApiRef = useRef(null);

  const columnDefs = useMemo(() => [
    {
      field: 'cse_name',
      headerName: 'CSE Name',
      minWidth: 160,
      flex: 2,
      cellStyle: { fontWeight: '600', color: '#111827' },
      valueFormatter: ({ value }) => value || '—',
      filter: 'agTextColumnFilter',
    },
    {
      field: 'p_count',
      headerName: 'Number of P',
      width: 120,
      minWidth: 90,
      cellRenderer: PCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      filter: false
    },
    {
      field: 'j_count',
      headerName: 'Number of J',
      width: 120,
      minWidth: 90,
      cellRenderer: JCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      filter: false
    },
    {
      field: 'total_projects',
      headerName: 'Total Projects',
      width: 160,
      minWidth: 140,
      cellStyle: { fontWeight: '600', color: '#1f2937', textAlign: 'center' },
      filter: false
    },
    {
      field: 'total_change_orders',
      headerName: 'Total CO#',
      width: 140,
      minWidth: 120,
      cellRenderer: COCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      filter: false,
      sort: 'desc'  // default sort: highest COs first
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 110,
      minWidth: 100,
      cellRenderer: StatusCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        valueFormatter: ({ value }) => value ? 'Active' : 'Inactive'
      }
    }
  ], []);

  const [isExporting, setIsExporting] = useState(false);

  // Build totals from an array of row objects
  const buildPinnedRow = useCallback((rows) => [{
    cse_name:            'GRAND TOTAL',
    p_count:             rows.reduce((s, r) => s + (r.p_count              || 0), 0),
    j_count:             rows.reduce((s, r) => s + (r.j_count              || 0), 0),
    total_projects:      rows.reduce((s, r) => s + (r.total_projects        || 0), 0),
    total_change_orders: rows.reduce((s, r) => s + (r.total_change_orders   || 0), 0),
    active:              null
  }], []);

  // Pinned row state — starts from full data, updated when grid filter changes
  const [pinnedBottomRow, setPinnedBottomRow] = useState(() => buildPinnedRow(data || []));

  // Recompute totals from only the filtered rows visible in the grid
  const recomputePinned = useCallback((api) => {
    const rows = [];
    api.forEachNodeAfterFilterAndSort(node => { 
      // Exclude pinned rows to avoid double-counting
      if (node.data && !node.rowPinned) rows.push(node.data); 
    });
    setPinnedBottomRow(buildPinnedRow(rows));
  }, [buildPinnedRow]);

  const handleGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    // Listen to filter changes and recompute grand total each time
    params.api.addEventListener('filterChanged', () => recomputePinned(params.api));
  }, [recomputePinned]);

  const getRowStyle = useCallback((params) => {
    if (params.node.rowPinned === 'bottom') {
      return {
        background: '#eff6ff',
        borderTop:  '2px solid #bfdbfe',
        fontWeight: '700',
        fontSize:   '13px',
        color:      '#1e3a5f'
      };
    }
    return undefined;
  }, []);

  const handleExportExcel = () => {
    setIsExporting(true);
    const rows = [];
    const api  = gridApiRef.current;

    if (api) {
      api.forEachNodeAfterFilterAndSort(node => {
        if (node.data) rows.push(node.data);
      });
    } else {
      rows.push(...(data || []));
    }

    if (rows.length === 0) {
      setIsExporting(false);
      return;
    }

    const exportData = rows.map(row => ({
      'CSE Name':       row.cse_name,
      'Number of P':    row.p_count,
      'Number of J':    row.j_count,
      'Total Projects': row.total_projects,
      'Total COs':      row.total_change_orders,
      'Status':         row.active ? 'Active' : 'Inactive'
    }));

    // Grand total row
    const totals = rows.reduce((acc, r) => ({
      p:  acc.p  + (r.p_count              || 0),
      j:  acc.j  + (r.j_count              || 0),
      tp: acc.tp + (r.total_projects        || 0),
      co: acc.co + (r.total_change_orders   || 0)
    }), { p: 0, j: 0, tp: 0, co: 0 });

    exportData.push({
      'CSE Name':       'GRAND TOTAL',
      'Number of P':    totals.p,
      'Number of J':    totals.j,
      'Total Projects': totals.tp,
      'Total COs':      totals.co,
      'Status':         ''
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CSE Statistics');
    worksheet['!cols'] = [
      { wch: 22 }, { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 12 }
    ];

    XLSX.writeFile(workbook, `cse-statistics-${new Date().toISOString().split('T')[0]}.xlsx`);
    setIsExporting(false);
  };

  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">Error loading CSE statistics</p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <p className="tw-text-gray-500 tw-text-center tw-py-8">No data available</p>
      </Card>
    );
  }

  return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      {/* Header */}
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-200 tw-bg-gradient-to-r tw-from-gray-50 tw-to-white tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <div className="tw-w-8 tw-h-8 tw-rounded tw-bg-violet-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
            <FiUsers className="tw-text-violet-600" size={16} />
          </div>
          <div>
            <h3 className="tw-text-base tw-font-bold tw-text-gray-900">CSE In-Charge Overview</h3>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
              {data.length} CSEs · click headers to sort · grand total pinned at bottom
            </p>
          </div>
        </div>

        <Button
          variant="secondary"
          leftIcon={FiDownload}
          loading={isExporting}
          onClick={handleExportExcel}
        >
          {isExporting ? 'Exporting…' : 'Export Excel'}
        </Button>
      </div>

      {/* Info alert — Total Projects caveat */}
      <div className="tw-my-3 tw-flex tw-items-start tw-gap-2.5 tw-px-3.5 tw-py-2.5 tw-rounded-lg tw-bg-blue-50 tw-border tw-border-blue-200">
        <FiInfo size={15} className="tw-text-blue-500 tw-flex-shrink-0 tw-mt-0.5" />
        <p className="tw-text-xs tw-text-blue-700 tw-leading-relaxed">
          <span className="tw-font-semibold">Note: </span>
          The <span className="tw-font-semibold">Total Projects</span> in the Grand Total row may be higher than the actual unique project count because a single project can be handled by multiple CSEs, causing it to be counted more than once across rows.
        </p>
      </div>

      {/* AG Grid with pinned grand-total row */}
      <AgGridTable
        rowData={data}
        columnDefs={columnDefs}
        serverSide={false}
        filter={true}
        sort={true}
        pagination={true}
        pageSize={10}
        loading={loading}
        rowHeight={48}
        headerHeight={44}
        enableCellTextSelection={true}
        pinnedBottomRowData={pinnedBottomRow}
        getRowStyle={getRowStyle}
        onGridReady={handleGridReady}
      />
    </Card>
  );
};

CSEStatisticsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cse_name:            PropTypes.string,
      p_count:             PropTypes.number,
      j_count:             PropTypes.number,
      total_projects:      PropTypes.number,
      total_change_orders: PropTypes.number,
      active:              PropTypes.bool
    })
  ),
  loading: PropTypes.bool,
  error:   PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

CSEStatisticsTable.defaultProps = {
  data:    [],
  loading: false,
  error:   null
};

export default CSEStatisticsTable;
